import { useEffect, useState, useRef } from 'react'
import _ from 'lodash'
import qs from 'query-string'
import {useUpdateEffect} from 'react-use'
import { useLocation, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { PRODUCT_PROPS_ID, PRODUCTS_PROPS_ID } from "src/common/razzle-platform-product/ssr"
import { fetchProduct, fetchProducts, fetchTagsOfProducts } from './effects'
import { IParamsOption, IRequestFetcherOptions, Pagination } from 'src/common/razzle-platform-components/types'
import { Tag } from 'src/common/razzle-platform-components/types'


function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
}

type UseProduct<T> = {
    product: T,
    isFirstRequestInPregress: boolean
}

export function useProduct<T>(options: Pick<IParamsOption, 'params'> = {}): UseProduct<T> {
    const params = useParams()
    const dispatch = useDispatch()
    const locale: string = useSelector((state: any) => state.translationModel.locale)
    // get the initial state of productProps by it sheared id
    const initialProps = useSelector((state: any) => state.ssrModel[PRODUCT_PROPS_ID]) || {}
    const [isFirstRequestInPregress, setFirstRequestInPregress] = useState(false)
    // we use this state to save our product
    const [state, setState] = useState({ product: initialProps.product })

    useEffect(() => {

        const id = _.get(params, 'id')

        if (initialProps.product) {
            // case when we make reload or refresh on the browser we get the saved product form the server (window.__PRELOADED_STATE__)

            // we remove all initialProps of productProps
            dispatch.ssrModel.cleanInitialProps(PRODUCT_PROPS_ID)
        } else {
            // case when we are on browser (pass from page to another) we don't need to get product form the server side

            // we make request if we don't have product or the product it's diffirente to saved product
            if (!state.product || state.product.id !== id) {
                
                // in product we need params for make forced request exmaple: parms: { _embed: [user, ...etc]}
                const queryOptions: Pick<IRequestFetcherOptions, 'params'> = {
                    params: options.params,
                }

                // set the Accept-Language to the headers of the request for get the translated data
		        const headers = { "Accept-Language": locale }

                const fetchData = async () => {
                    setFirstRequestInPregress(true)
                    // get the product by execute the effect fetchProduct and pass it queryOptions
                    const { product } = await fetchProduct(id, queryOptions, headers)
                    // save the product in our state
                    setState({ product })
                    setFirstRequestInPregress(false)
                }
                fetchData()
            }
        }

	}, [])

    return { product: state.product, isFirstRequestInPregress }

}

type UseProducts<T> = {
    products: Array<T>,
    pagination: Pagination,
    isFirstRequestInPregress: boolean,
    isUpdateProgress: boolean
}

export function useProducts<T>(options: IParamsOption={params: null, useUrlParams: true, usePagination: true }): UseProducts<T>  {
    const location = useLocation()
    const dispatch = useDispatch()
    const locale: string = useSelector((state: any) => state.translationModel.locale)
    // get the initial state of productsProps by it sheared id
    const initialProps = useSelector((state: any) => state.ssrModel[PRODUCTS_PROPS_ID]) || {}
    // state use when we update the products (change page, filters, ...ect)
    const [isUpdateProgress, setUpdateInProgress] = useState(false)
    // use this state in the first render for use loader page
    const [isFirstRequestInPregress, setFirstRequestInPregress] = useState(false)
    // we use this state to save our products and thier pagintion
    const [state, setState] = useState({ products: initialProps.products, pagination: initialProps.pagination })
    const prevSearch = usePrevious(location.search)

    const queryOptions: IRequestFetcherOptions = {
        // prams given from url
        searchParams: qs.parse(location.search),
        // forced params by user not it's shown in url
        params: options.params,
        // to allow use pagination
        usePagination: options.usePagination,
        // to allow use search parms from url
        useUrlParams: options.useUrlParams,
    }

    // set the Accept-Language to the headers of the request for get the translated data
    const headers = { "Accept-Language": locale }

    useEffect(() => {

        if (initialProps.products) {
            // case when we make reload or refresh on the browser we get the saved store form the server (window.__PRELOADED_STATE__)

            // we remove all initialProps of storeProps
            dispatch.ssrModel.cleanInitialProps(PRODUCTS_PROPS_ID)
        } else {
            // case when we are on browser (pass from page to another) we don't need to get store form the server side
            const fetchData = async () => {
                setFirstRequestInPregress(true)
                // get the products by execute the effect fetchProducts and pass it queryOptions
                const {products, pagination} = await fetchProducts(queryOptions, headers)
                // save the products in our state
                setState({ products, pagination })
                setFirstRequestInPregress(false)
            }
            fetchData()
        }
    }, [])

    useUpdateEffect(() => {
        // we use this effects when we update the url (change the page, filters, ...etc)

        // check if the update by url is allowed
        if (options.useUrlParams) {

            const fetchData = async () => {
                setUpdateInProgress(true)
                const {products, pagination} = await fetchProducts(queryOptions, headers)
                const newState = {
                    pagination,
                    products: []
                }
                if (options.infiniteScroll) {
                    const searchQuery = qs.parse(location.search)
                    const previousSearchQuery = qs.parse(prevSearch)
                    if (_.isEqual(_.omit(searchQuery, ['_page', '_per_page']), _.omit(previousSearchQuery, ['_page', '_per_page']))) {
                        newState.products = _.unionWith(state.products, products, (stateItem: any, item: any) => stateItem.id === item.id )
                    } else {
                        newState.products = products
                    }

                } else {
                    newState.products = products
                }

                setState(newState)
                setUpdateInProgress(false)
            }
            fetchData()
        }

    }, [location.search])

    return {
        products: state.products,
        pagination: state.pagination,
        isFirstRequestInPregress,
        isUpdateProgress
    }
}


export type UseTagsOfProducts<T = Tag> = {
    tagsOfProducts: Array<T>,
    inProgressTagsOfProducts: boolean
}

// we use ths function to get the tags of products on the client side
export function useTagsOfProducts<T = Tag>(options: Pick<IParamsOption, 'params'>  = {}):  UseTagsOfProducts<T> {
    const locale: string = useSelector((state: any) => state.translationModel.locale)
    const [inProgress, setInProgress] = useState(false)
    // we use this state to save our tags of products
    const [state, setState] = useState({ tagsOfProducts: [] })

    useEffect(() => {
        // in tags of products we need params for make forced request exmaple: parms: { _embed: [user, ...etc]}
        const queryOptions: Pick<IRequestFetcherOptions, 'params'> = {
            params: options.params,
        }

        // case when we are on browser (pass from page to another) we don't need to get tags of products form the server side
        const fetchData = async () => {
            setInProgress(true)
            const headers = { "Accept-Language": locale }
            try {
                // get the tags of products by execute the effect fetchTagsOfProducts and pass it queryOptions
                const { tagsOfProducts } = await fetchTagsOfProducts(queryOptions, headers)
                // save the tags of products in our state
                setState({ tagsOfProducts })
            } catch (error) {
                console.error(error)
            } finally {
                setInProgress(false)
            }
        }
        fetchData()
	}, [])

    return { tagsOfProducts: state.tagsOfProducts, inProgressTagsOfProducts: inProgress }
}